// src/pages/JewelleryPlans/Redemptions/BalanceCheck.jsx

import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  checkBalanceThunk,
  createBalanceQuoteThunk,
} from "../../../store/thunks/jewelleryPlan/quoteThunks";
import { getPlanByIdThunk } from "../../../store/thunks/jewelleryPlan/planThunks";
import {
  ScaleIcon,
  CurrencyRupeeIcon,
  SparklesIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationIcon,
  LockClosedIcon,
  InformationCircleIcon,
  CalculatorIcon,
  ShoppingBagIcon,
} from "@heroicons/react/outline";

const BalanceCheck = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { balance, balanceLoading, createLoading, balanceError } = useSelector(
    (state) => state.jewelleryPlan?.quotes
  );
  const { selectedPlan } = useSelector((state) => state.jewelleryPlan?.plans);

  const [selectedKarat, setSelectedKarat] = useState(22);
  const [isLoading, setIsLoading] = useState(true);

  // User input for custom calculation
  const [customGrams, setCustomGrams] = useState("");
  const [showCalculator, setShowCalculator] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, [planId]);

  useEffect(() => {
    if (planId && balance) {
      // For VALUE_SAVINGS, karat is fixed at 22K as per alignment doc
      const karatToUse =
        balance?.planDetails?.planType === "VALUE_SAVINGS" ? 22 : selectedKarat;
      dispatch(checkBalanceThunk({ planId, karat: karatToUse }));
    }
  }, [selectedKarat, planId, dispatch]);

  const fetchInitialData = async () => {
    try {
      setIsLoading(true);
      await dispatch(getPlanByIdThunk(planId)).unwrap();
      // Initial balance check
      await dispatch(
        checkBalanceThunk({ planId, karat: selectedKarat })
      ).unwrap();
    } catch (error) {
      console.error("Failed to fetch plan:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate how much gold user can buy with their wallet
  // Aligned with Quote breakdown structure from alignment doc
  const purchasableGold = useMemo(() => {
    if (!balance) return null;

    const { purchasingPower, benefits, planDetails } = balance;
    const planType = planDetails?.planType;

    // Get pricing rule from appropriate location based on plan type
    // As per alignment doc: breakdown.pricingRuleSnapshot is used
    const pricingRule =
      purchasingPower.examplePurchase?.pricingRuleApplied ||
      purchasingPower.estimatedAfterCharges?.pricingRuleApplied ||
      purchasingPower.pricingRuleSnapshot;

    if (!pricingRule) return null;

    const { chargeBreakdown, gstConfig } = pricingRule;

    // Get wallet value based on plan type (aligned with breakdown structure)
    const walletValue =
      planType === "VALUE_SAVINGS"
        ? balance.balance.valueInr || purchasingPower.totalValue || 0
        : purchasingPower.totalValue || purchasingPower.lockedValue || 0;

    // Get rate based on plan type and rate strategy
    // Aligned with rateSnapshot.strategy enum values
    let ratePerGram = 0;
    if (planType === "PRICE_LOCK") {
      // PRICE_LOCKED strategy - use locked rate
      ratePerGram = purchasingPower.lockedRate?.ratePerGram || 0;
    } else if (planType === "WEIGHT_SAVINGS") {
      // Could be CURRENT_WITH_BENEFITS or MATURITY_LOCKED
      ratePerGram =
        purchasingPower.currentRate?.ratePerGram ||
        purchasingPower.lockedRate?.ratePerGram ||
        0;
    } else {
      // VALUE_SAVINGS - uses current rate
      ratePerGram =
        purchasingPower.currentGoldRate?.ratePerGram ||
        purchasingPower.currentRate?.ratePerGram ||
        0;
    }

    if (!ratePerGram) return null;

    // Get charge percentages from pricingRuleSnapshot
    let makingPercent = chargeBreakdown?.makingRate || 8;
    if (chargeBreakdown?.makingType !== "PERCENTAGE") {
      makingPercent = 8; // Fallback for per-gram or fixed
    }

    const wastagePercent = chargeBreakdown?.wastageRate || 2;
    const gstPercent = gstConfig?.customRate || 3;

    // Apply benefit waiver if applicable
    // Aligned with benefitsCalculation structure from alignment doc
    let effectiveMakingPercent = makingPercent;
    if (benefits.applicable) {
      if (planType === "VALUE_SAVINGS" && benefits.waivePercent) {
        // MAKING_CHARGE_WAIVER benefit type
        effectiveMakingPercent =
          makingPercent * (1 - benefits.waivePercent / 100);
      }
      // For WEIGHT_SAVINGS/PRICE_LOCK, bonus grams are already added to totalGrams
    }

    // Calculate total multiplier including GST
    const baseMultiplier =
      1 + effectiveMakingPercent / 100 + wastagePercent / 100;
    const gstMultiplier =
      gstConfig?.includesMaking !== false &&
      gstConfig?.includesWastage !== false
        ? 1 + gstPercent / 100
        : 1;
    const totalMultiplier = baseMultiplier * gstMultiplier;

    // Calculate max purchasable grams
    const effectiveGoldValue = walletValue / totalMultiplier;
    const maxGrams = effectiveGoldValue / ratePerGram;

    return {
      maxGrams: Math.floor(maxGrams * 1000) / 1000,
      ratePerGram,
      effectiveMakingPercent,
      wastagePercent,
      gstPercent,
      totalMultiplier,
      walletValue,
      planType,
    };
  }, [balance]);

  // Calculate cost for custom gram input
  const customCalculation = useMemo(() => {
    if (!customGrams || !purchasableGold) return null;

    const grams = parseFloat(customGrams);
    if (isNaN(grams) || grams <= 0) return null;

    const {
      ratePerGram,
      effectiveMakingPercent,
      wastagePercent,
      gstPercent,
      walletValue,
    } = purchasableGold;

    const goldValue = grams * ratePerGram;
    const makingCharges = goldValue * (effectiveMakingPercent / 100);
    const wastageCharges = goldValue * (wastagePercent / 100);
    const subtotal = goldValue + makingCharges + wastageCharges;
    const gst = subtotal * (gstPercent / 100);
    const total = subtotal + gst;

    const difference = total - walletValue;

    return {
      grams,
      goldValue: Math.round(goldValue),
      makingCharges: Math.round(makingCharges),
      wastageCharges: Math.round(wastageCharges),
      gst: Math.round(gst),
      total: Math.round(total),
      walletCovers: walletValue,
      extraToPay: difference > 0 ? Math.round(difference) : 0,
      walletRemaining: difference < 0 ? Math.round(Math.abs(difference)) : 0,
    };
  }, [customGrams, purchasableGold]);

  const handleCreateQuote = async () => {
    try {
      const result = await dispatch(
        createBalanceQuoteThunk({
          planId,
          karat: selectedKarat,
          lockMinutes: 30,
        })
      ).unwrap();

      const quoteId = result.data?._id || result._id;

      // Validate quote has expected planType (as recommended in alignment doc)
      if (result.data?.planType && selectedPlan?.templateSnapshot?.type) {
        if (result.data.planType !== selectedPlan.templateSnapshot.type) {
          console.warn("Quote plan type mismatch detected");
        }
      }

      navigate(`/jewellery/${planId}/quote/${quoteId}`);
    } catch (error) {
      console.error("Failed to create quote:", error);
    }
  };

  const getStatusIcon = (tier) => {
    // Aligned with redemptionContext.tier enum values
    switch (tier) {
      case "WITHIN_WINDOW":
        return <CheckCircleIcon className="h-6 w-6 text-green-600" />;
      case "GRACE_PERIOD":
        return <ExclamationIcon className="h-6 w-6 text-yellow-600" />;
      case "EXPIRED":
        return <ExclamationIcon className="h-6 w-6 text-red-600" />;
      case "NOT_MATURED":
        return <ClockIcon className="h-6 w-6 text-blue-600" />;
      default:
        return <ClockIcon className="h-6 w-6 text-gray-400" />;
    }
  };

  const getStatusColor = (tier) => {
    switch (tier) {
      case "WITHIN_WINDOW":
        return "green";
      case "GRACE_PERIOD":
        return "yellow";
      case "EXPIRED":
        return "red";
      case "NOT_MATURED":
        return "blue";
      default:
        return "gray";
    }
  };

  const getPlanTypeDisplay = (type) => {
    // Aligned with planType enum values
    switch (type) {
      case "VALUE_SAVINGS":
        return "Fixed Value Plan";
      case "WEIGHT_SAVINGS":
        return "Gold Weight Plan";
      case "PRICE_LOCK":
        return "Price Lock Plan";
      default:
        return type;
    }
  };

  const getBenefitTypeDisplay = (benefits, planType) => {
    // Aligned with BENEFIT_TYPES enum
    if (!benefits?.applicable) return null;

    if (planType === "VALUE_SAVINGS") {
      return {
        type: "MAKING_CHARGE_WAIVER",
        label: `${benefits.waivePercent}% OFF`,
        description: "Making Charges",
      };
    } else if (planType === "WEIGHT_SAVINGS" || planType === "PRICE_LOCK") {
      return {
        type: "BONUS_GRAMS",
        label: `+${benefits.grams}g`,
        description: "Bonus Gold",
      };
    }
    return null;
  };

  if (isLoading || balanceLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  if (!selectedPlan) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-500">Plan not found</p>
          <button
            onClick={() => navigate("/jewellery/active")}
            className="mt-4 text-yellow-600 hover:text-yellow-700"
          >
            Back to Plans
          </button>
        </div>
      </div>
    );
  }

  if (balanceError) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{balanceError}</p>
        </div>
      </div>
    );
  }

  if (!balance) return null;

  const {
    planDetails,
    balance: balanceData,
    purchasingPower,
    benefits,
    redemptionContext,
  } = balance;

  const statusColor = getStatusColor(redemptionContext.tier);
  const planType = planDetails.planType;
  const isValuePlan = planType === "VALUE_SAVINGS";
  const isPriceLockPlan = planType === "PRICE_LOCK";
  const isWeightPlan = planType === "WEIGHT_SAVINGS";
  const benefitDisplay = getBenefitTypeDisplay(benefits, planType);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/jewellery/${planId}`)}
          className="text-gray-500 hover:text-gray-700 mb-4"
        >
          ← Back to Plan
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          Your Redemption Options
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Plan #{planDetails.planNumber} • {getPlanTypeDisplay(planType)}
        </p>
      </div>

      {/* Status Banner - Uses redemptionContext.tier enum */}
      <div
        className={`mb-6 bg-${statusColor}-50 border border-${statusColor}-200 rounded-lg p-4`}
      >
        <div className="flex items-center">
          {getStatusIcon(redemptionContext.tier)}
          <div className="ml-3">
            <p className={`font-medium text-${statusColor}-900`}>
              {redemptionContext.message}
            </p>
            {redemptionContext.timeRemaining && (
              <p className="text-sm text-gray-600 mt-1">
                {redemptionContext.timeRemaining}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* MAIN CARD: What You Have */}
      <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-2 border-yellow-300 rounded-xl p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              {isValuePlan ? "Your Wallet Balance" : "Your Gold Balance"}
            </h2>

            {isValuePlan ? (
              <div>
                <p className="text-4xl font-bold text-gray-900">
                  ₹{(balanceData.valueInr || 0).toLocaleString()}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Fixed savings • Not affected by gold price changes
                </p>
              </div>
            ) : (
              <div>
                <p className="text-4xl font-bold text-gray-900">
                  {balanceData.totalGrams || 0}g
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  Worth ₹
                  {(
                    purchasingPower.totalValue ||
                    purchasingPower.lockedValue ||
                    0
                  ).toLocaleString()}{" "}
                  today @ ₹
                  {(
                    purchasingPower.currentRate?.ratePerGram ||
                    purchasingPower.lockedRate?.ratePerGram ||
                    0
                  ).toLocaleString()}
                  /g
                </p>
                {isPriceLockPlan && (
                  <div className="flex items-center mt-2 text-green-700">
                    <LockClosedIcon className="h-4 w-4 mr-1" />
                    <span className="text-xs font-medium">
                      Rate locked - protected from market changes
                    </span>
                  </div>
                )}
                {isWeightPlan && benefits.applicable && (
                  <div className="flex items-center mt-2 text-purple-700">
                    <SparklesIcon className="h-4 w-4 mr-1" />
                    <span className="text-xs font-medium">
                      Includes {benefits.grams || 0}g bonus gold
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Benefits Badge - Aligned with benefitType enum */}
          {benefitDisplay && (
            <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-center">
              <SparklesIcon className="h-8 w-8 text-purple-600 mx-auto" />
              <p className="text-lg font-bold text-purple-700 mt-1">
                {benefitDisplay.label}
              </p>
              <p className="text-xs text-purple-600">
                {benefitDisplay.description}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* KEY INSIGHT: How Much Gold Can You Buy? */}
      {purchasableGold && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
          <div className="flex items-center mb-4">
            <ShoppingBagIcon className="h-6 w-6 text-green-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              How Much Gold Can You Buy?
            </h3>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <p className="text-sm text-gray-600 mb-2">
              With your {isValuePlan ? "wallet balance" : "gold value"} of ₹
              {purchasableGold.walletValue.toLocaleString()}, you can purchase:
            </p>
            <p className="text-5xl font-bold text-green-700">
              {purchasableGold.maxGrams}g
            </p>
            <p className="text-sm text-gray-600 mt-2">
              of {selectedKarat}K gold jewelry (including all charges)
            </p>

            {benefits.applicable && isValuePlan && (
              <div className="mt-3 bg-purple-50 border border-purple-200 rounded p-2">
                <p className="text-xs text-purple-700">
                  ✨ Your {benefits.waivePercent}% making charge waiver is
                  already applied!
                </p>
              </div>
            )}
          </div>

          {/* Rate Reference */}
          <div className="mt-4 flex justify-between text-sm text-gray-600">
            <span>{isPriceLockPlan ? "Locked Rate:" : "Current Rate:"}</span>
            <span className="font-medium">
              ₹{purchasableGold.ratePerGram.toLocaleString()}/gram (
              {selectedKarat}K)
            </span>
          </div>

          {/* Karat Selector - Uses karat enum [14, 18, 22, 24] */}
          {!isValuePlan && (
            <div className="mt-4">
              <label className="text-sm text-gray-600 block mb-2">
                Change Karat:
              </label>
              <div className="flex gap-2">
                {[24, 22, 18, 14].map((k) => (
                  <button
                    key={k}
                    onClick={() => setSelectedKarat(k)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium ${
                      selectedKarat === k
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {k}K
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* CALCULATOR: Custom Amount */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
        <button
          onClick={() => setShowCalculator(!showCalculator)}
          className="w-full flex items-center justify-between"
        >
          <div className="flex items-center">
            <CalculatorIcon className="h-6 w-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">
              Calculate for Specific Amount
            </h3>
          </div>
          <span className="text-sm text-blue-600">
            {showCalculator ? "Hide" : "Show"} Calculator
          </span>
        </button>

        {showCalculator && (
          <div className="mt-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <label className="block text-sm text-gray-600 mb-1">
                  I want to buy (grams):
                </label>
                <input
                  type="number"
                  value={customGrams}
                  onChange={(e) => setCustomGrams(e.target.value)}
                  placeholder="e.g., 5"
                  step="0.1"
                  min="0"
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="text-center pt-6">
                <span className="text-2xl text-gray-400">→</span>
              </div>
            </div>

            {customCalculation && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-3">
                  For {customCalculation.grams}g of {selectedKarat}K Gold:
                </h4>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gold Value:</span>
                    <span className="font-medium">
                      ₹{customCalculation.goldValue.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Making Charges:</span>
                    <span className="font-medium">
                      ₹{customCalculation.makingCharges.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Wastage:</span>
                    <span className="font-medium">
                      ₹{customCalculation.wastageCharges.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">GST:</span>
                    <span className="font-medium">
                      ₹{customCalculation.gst.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600">Total Cost:</span>
                    <span className="font-bold text-gray-900">
                      ₹{customCalculation.total.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Your Balance:</span>
                    <span className="font-medium text-green-600">
                      - ₹{customCalculation.walletCovers.toLocaleString()}
                    </span>
                  </div>

                  <div className="border-t pt-2 mt-2">
                    {customCalculation.extraToPay > 0 ? (
                      <div className="flex justify-between">
                        <span className="text-red-700 font-medium">
                          You Need to Pay:
                        </span>
                        <span className="text-xl font-bold text-red-700">
                          ₹{customCalculation.extraToPay.toLocaleString()}
                        </span>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-green-700 font-medium">
                          Balance Remaining:
                        </span>
                        <span className="text-xl font-bold text-green-700">
                          ₹{customCalculation.walletRemaining.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Quick suggestions */}
                <div className="mt-4 pt-3 border-t border-blue-200">
                  <p className="text-xs text-gray-600 mb-2">Quick options:</p>
                  <div className="flex gap-2 flex-wrap">
                    {purchasableGold &&
                      [
                        Math.floor(purchasableGold.maxGrams),
                        Math.floor(purchasableGold.maxGrams * 0.75),
                        Math.floor(purchasableGold.maxGrams * 0.5),
                        Math.floor(purchasableGold.maxGrams * 1.5),
                      ]
                        .filter((g) => g > 0)
                        .sort((a, b) => a - b)
                        .map((g) => (
                          <button
                            key={g}
                            onClick={() => setCustomGrams(g.toString())}
                            className="px-3 py-1 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50"
                          >
                            {g}g
                          </button>
                        ))}
                  </div>
                </div>
              </div>
            )}

            {!customCalculation && customGrams && (
              <div className="text-sm text-gray-500 text-center py-4">
                Enter a valid amount to see calculation
              </div>
            )}
          </div>
        )}
      </div>

      {/* IMPORTANT INFO for different plan types */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <InformationCircleIcon className="h-5 w-5 text-gray-500 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            {isValuePlan && (
              <>
                <p className="font-medium mb-1">Fixed Value Plan Benefits:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>
                    Your ₹{(balanceData.valueInr || 0).toLocaleString()} is
                    guaranteed
                  </li>
                  <li>Not affected by gold price fluctuations</li>
                  {benefits.applicable && (
                    <li className="text-purple-700 font-medium">
                      {benefits.waivePercent}% waiver on making charges - you
                      save more!
                    </li>
                  )}
                  <li>
                    Rate strategy: Current market rates at time of redemption
                  </li>
                </ul>
              </>
            )}
            {isWeightPlan && (
              <>
                <p className="font-medium mb-1">Gold Weight Plan Benefits:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>You own {balanceData.totalGrams || 0}g of actual gold</li>
                  <li>Value increases with gold price</li>
                  {benefits.applicable && (
                    <li className="text-purple-700 font-medium">
                      +{benefits.grams}g bonus gold added to your balance!
                    </li>
                  )}
                  <li>
                    {redemptionContext.tier === "EXPIRED"
                      ? "Rate locked at maturity date"
                      : "Rate based on current market with benefits"}
                  </li>
                </ul>
              </>
            )}
            {isPriceLockPlan && (
              <>
                <p className="font-medium mb-1">Price Lock Plan Benefits:</p>
                <ul className="list-disc ml-4 space-y-1">
                  <li>Your rate is locked - protected from market changes</li>
                  <li>
                    Buy at ₹
                    {(
                      purchasingPower.lockedRate?.ratePerGram || 0
                    ).toLocaleString()}
                    /g regardless of current price
                  </li>
                  {benefits.applicable && (
                    <li className="text-purple-700 font-medium">
                      +{benefits.grams}g bonus gold added!
                    </li>
                  )}
                  <li>Rate strategy: Price locked at plan enrollment</li>
                </ul>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={() => navigate(`/jewellery/${planId}`)}
          className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
        >
          Back to Plan
        </button>
        <button
          onClick={handleCreateQuote}
          //   disabled={
          //     createLoading ||
          //     redemptionContext.tier === "EXPIRED" ||
          //     redemptionContext.tier === "NOT_MATURED"
          //   }
          className="flex-1 px-6 py-3 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center"
        >
          {createLoading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                />
              </svg>
              Creating Quote...
            </>
          ) : (
            <>
              <LockClosedIcon className="h-5 w-5 mr-2" />
              Proceed to Redeem
            </>
          )}
        </button>
      </div>

      {/* Warning for NOT_MATURED tier */}
      {redemptionContext.tier === "NOT_MATURED" && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
          <p className="text-sm text-blue-700">
            Plan has not matured yet. Redemption will be available after
            maturity date.
          </p>
        </div>
      )}
    </div>
  );
};

export default BalanceCheck;
